import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Upload } from "lucide-react";

import { enviarMidia } from "@/components/admin/enviarMidia";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { validarMidia, type ErrosArtigo } from "@/lib/admin/validacao";

type Item = {
  id: string;
  path: string;
  alt: string;
  width: number | null;
  height: number | null;
  mime: string | null;
  size_bytes: number | null;
};

export const Route = createFileRoute("/admin/midia")({
  ssr: false,
  head: () => ({ meta: [{ title: "Mídia | Painel" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: Biblioteca,
});

const campo =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring";
const erroTexto = "mt-1 text-xs font-medium text-destructive";

function tamanho(bytes: number | null): string {
  if (!bytes) return "—";
  return `${Math.round(bytes / 1024)} KB`;
}

function Biblioteca() {
  const [itens, setItens] = useState<Item[]>([]);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [alt, setAlt] = useState("");
  const [erros, setErros] = useState<ErrosArtigo>({});
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    void supabase
      .from("media")
      .select("id, path, alt, width, height, mime, size_bytes")
      .order("created_at", { ascending: false })
      .then(({ data }) => setItens((data ?? []) as Item[]));
  }, []);

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    setMensagem(null);
    // O banco recusa imagem sem texto alternativo, mas o usuário não pode ver
    // erro de banco: o formulário recusa antes, em português.
    const problemas = validarMidia({ alt, arquivo });
    setErros(problemas);
    if (Object.keys(problemas).length > 0) {
      setMensagem("O envio foi recusado. Corrija os campos marcados.");
      return;
    }

    setOcupado(true);
    try {
      if (!arquivo) return;
      const salva = await enviarMidia(arquivo, alt);
      setItens((lista) => [salva, ...lista]);
      setArquivo(null);
      setAlt("");
      setMensagem("Arquivo enviado.");
    } catch (erro) {
      setMensagem(`Não foi possível enviar: ${(erro as Error).message}`);
    } finally {
      setOcupado(false);
    }
  }

  async function salvarDescricao(item: Item) {
    const texto = item.alt.trim();
    if (!texto) {
      setMensagem("O texto alternativo ou a descrição do PDF não pode ficar vazio.");
      return;
    }
    const { error } = await supabase
      .from("media")
      .update({ alt: texto })
      .eq("id", item.id);
    setMensagem(error ? `Não foi possível salvar: ${error.message}` : "Descrição da mídia atualizada.");
  }

  return (
    <section>
      <h1 className="text-lg font-semibold text-foreground">Biblioteca de mídia</h1>
      <p className="mt-2 text-sm text-muted-foreground">Envie imagens e PDFs usados no site. Cada arquivo ganha um link fixo do próprio site, que você pode copiar e colar em artigos e páginas.</p>

      <form onSubmit={enviar} className="mt-6 max-w-md space-y-4 rounded-md border border-border p-4" noValidate>
        {mensagem ? (
          <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground">{mensagem}</p>
        ) : null}
        <div>
          <label className="block text-sm font-medium text-foreground" htmlFor="arquivo">Arquivo</label>
          <input
            id="arquivo"
            type="file"
            accept="image/*,application/pdf"
            className={campo}
            onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
          />
          {erros["arquivo"] ? <p className={erroTexto}>{erros["arquivo"]}</p> : null}
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground" htmlFor="alt">
            Texto alternativo ou descrição do PDF (obrigatório)
          </label>
          <input id="alt" className={campo} value={alt} onChange={(e) => setAlt(e.target.value)} />
          {erros["alt"] ? <p className={erroTexto}>{erros["alt"]}</p> : null}
        </div>
        <Button
          type="submit"
          disabled={ocupado}
        >
          <Upload className="mr-2 size-4" aria-hidden="true" /> {ocupado ? "Enviando..." : "Enviar arquivo"}
        </Button>
      </form>

      {itens.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">Nenhum arquivo enviado.</p>
      ) : (
        <ul className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3">
          {itens.map((item) => (
            <li key={item.id} className="rounded-md border border-border p-3">
              {item.mime === "application/pdf" ? (
                <div className="flex aspect-video items-center justify-center rounded bg-muted text-sm font-semibold text-muted-foreground">PDF</div>
              ) : (
                <img
                  src={item.path}
                  alt={item.alt}
                  width={item.width ?? 320}
                  height={item.height ?? 180}
                  loading="lazy"
                  className="h-auto w-full rounded"
                />
              )}
              <label className="mt-2 block text-xs font-medium text-foreground" htmlFor={`alt-${item.id}`}>
                Texto alternativo ou descrição
              </label>
              <input
                id={`alt-${item.id}`}
                className={campo}
                value={item.alt}
                onChange={(event) => setItens((current) => current.map((candidate) => candidate.id === item.id ? { ...candidate, alt: event.target.value } : candidate))}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {item.width ?? "?"}×{item.height ?? "?"} · {item.mime ?? "—"} · {tamanho(item.size_bytes)}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button type="button" size="sm" onClick={() => void salvarDescricao(item)}>Salvar descrição</Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void navigator.clipboard.writeText(`${window.location.origin}${item.path}`).then(() => setMensagem("Link copiado."))}
                >
                  Copiar link
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
